// packages
import { create } from 'zustand'

// libs
import { api } from '@/lib/axios'

// entities
import { SignsSymptomProps, signs_symptom_category, signs_symptom_children_range, SymptomChecks, TrimesterChecks } from '@/entities/signs-symptom'

// constants
import { HTTP_STATUS_CODE } from '@/constants/http'

// store
import { useAuthStore } from './auth'

export interface SignsSymptomsState {
  items: SignsSymptomProps[]
  isLoading: boolean
  fetchAll: () => Promise<void>
  fetchByRef: (ref: string) => Promise<SignsSymptomProps | null>
  create: (input: CreateInput) => Promise<SignsSymptomProps | null>
  update: (ref: string, input: Partial<CreateInput>) => Promise<SignsSymptomProps | null>
  remove: (ref: string) => Promise<boolean>
  generateAnalysis: (ref: string) => Promise<{ ref: string; historyRef: string } | { error: string } | null>
  setLocal: (record: SignsSymptomProps) => void
}

export interface CreateInput {
  patientName: string
  category: signs_symptom_category
  weeksOfGestation?: number | null
  childrenRange?: signs_symptom_children_range | null
  generalSymptoms?: SymptomChecks
  generalSymptomsResolved?: Record<string, { name: string; system?: string }>
  /** Sintomas gestacionais por trimestre (apenas categoria GESTANTE). */
  gestationalSymptoms?: TrimesterChecks | null
  /** Mapa resolvido: `{ TRIM1: { [id]: { name } }, ... }` */
  gestationalSymptomsResolved?: Record<string, Record<string, { name: string }>> | null
}

const userRef = () => useAuthStore.getState().user?.ref

export const useSignsSymptomsStore = create<SignsSymptomsState>()((set, get) => ({
  items: [],
  isLoading: false,

  fetchAll: async () => {
    const ref = userRef()
    if (!ref) return
    set({ isLoading: true })
    try {
      const response = await api.get('/', {
        params: { action: 'signs-symptoms', userRef: ref, page: 0, limit: 50 }
      })
      if (response.data.statusCode === HTTP_STATUS_CODE.OK) {
        const body = response.data.body
        // body é [items, total]
        const items: SignsSymptomProps[] = Array.isArray(body?.[0]) ? body[0] : []
        set({ items })
      }
    } finally {
      set({ isLoading: false })
    }
  },

  fetchByRef: async ref => {
    const userRefVal = userRef()
    if (!userRefVal) return null
    const response = await api.get('/', { params: { action: 'signs-symptoms-by-ref', userRef: userRefVal, ref } })
    if (response.data.statusCode !== HTTP_STATUS_CODE.OK) return null
    const item: SignsSymptomProps | undefined = response.data.body?.[0]
    if (!item) return null
    // sincroniza no store
    const current = get().items.filter(i => i.ref !== ref)
    set({ items: [item, ...current] })
    return item
  },

  create: async input => {
    const ref = userRef()
    if (!ref) return null
    const response = await api.post('/', {
      action: 'signs-symptoms',
      userRef: ref,
      patientName: input.patientName,
      category: input.category,
      weeksOfGestation: input.weeksOfGestation ?? null,
      childrenRange: input.childrenRange ?? null,
      generalSymptoms: input.generalSymptoms ?? {},
      generalSymptomsResolved: input.generalSymptomsResolved ?? {},
      gestationalSymptoms: input.gestationalSymptoms ?? null,
      gestationalSymptomsResolved: input.gestationalSymptomsResolved ?? null
    })
    if (response.data.statusCode !== HTTP_STATUS_CODE.CREATED) return null
    const created: { ref: string } | undefined = response.data.body?.[0]
    if (!created?.ref) return null
    return await get().fetchByRef(created.ref)
  },

  update: async (ref, input) => {
    const userRefVal = userRef()
    if (!userRefVal) return null
    const response = await api.put('/', {
      action: 'signs-symptoms',
      userRef: userRefVal,
      ref,
      ...input
    })
    if (response.data.statusCode !== HTTP_STATUS_CODE.OK) return null
    return await get().fetchByRef(ref)
  },

  remove: async ref => {
    const userRefVal = userRef()
    if (!userRefVal) return false
    const response = await api.delete('/', { params: { action: 'signs-symptoms', userRef: userRefVal, ref } })
    if (response.data.statusCode !== HTTP_STATUS_CODE.OK) return false
    set(state => ({ items: state.items.filter(i => i.ref !== ref) }))
    return true
  },

  generateAnalysis: async ref => {
    const userRefVal = userRef()
    if (!userRefVal) return null
    const response = await api.post('/', {
      action: 'generate-signs-symptoms-analysis',
      userRef: userRefVal,
      ref
    })
    if (response.data.statusCode === HTTP_STATUS_CODE.CONFLICT) {
      return { error: response.data.statusMessage || 'Operação não permitida.' }
    }
    if (response.data.statusCode !== HTTP_STATUS_CODE.CREATED) {
      return { error: response.data.statusMessage || 'Falha ao gerar análise.' }
    }
    // Re-busca para refletir status PROCESSING
    await get().fetchByRef(ref)
    return response.data.body?.[0] ?? null
  },

  setLocal: record => {
    set(state => {
      const idx = state.items.findIndex(i => i.ref === record.ref)
      if (idx === -1) return { items: [record, ...state.items] }
      const arr = [...state.items]
      arr[idx] = record
      return { items: arr }
    })
  }
}))
